<template>
    <div id="settings-page">
        <confirm id="confirmDeleteQuotationField" ref="confirmDeleteQuotationField" @confirm="deleteQuotationField(quotationFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('quotationFields-delete_modal-title') }}
                    <b>"{{ quotationFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                <template v-if="segmentsRelations.length === 0 && automationsRelations.length === 0">
                    {{ t('quotationFields-delete_modal-body_standard') }}
                </template>
                <template v-else>
                    {{ t('quotationFields-delete_modal-body_linked') }}
                    <template v-if="automationsRelations.length > 0">
                        {{ t('quotationFields-delete_modal-automations') }}
                        <ul>
                            <li v-for="automation in automationsRelations" :key="automation.title">
                                {{ automation.title }}
                            </li>
                        </ul>
                    </template>
                    <template v-if="segmentsRelations.length > 0">
                        {{ t('quotationFields-delete_modal-segments') }}
                        <ul>
                            <li v-for="segment in segmentsRelations" :key="segment.name">
                                {{ segment.name }}
                            </li>
                        </ul>
                    </template>
                    {{ t('quotationFields-delete_modal-warning') }}
                </template>
            </template>
        </confirm>
        <confirm id="confirmEditQuotationField" ref="confirmEditQuotationField" @confirm="editQuotationFieldName(quotationFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('quotationFields-edit_modal-title') }}
                    <b>"{{ quotationFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                {{ t('quotationFields-edit_modal-body') }}
            </template>
        </confirm>
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12 col-sm-12">
                    <div class="box clearfix">
                        <template v-if="state === 'loading'">
                            <loader :inline="true"></loader>
                        </template>
                        <template v-if="state === 'list'">
                            <h1>{{ t('quotationFields-title') }}</h1>
                            <table class="table table-striped fields mt-4">
                                <tr v-for="field in fields" :key="field.id">
                                    <td>
                                        <div class="field-list-item d-flex align-items-center">
                                            <div class="field-icon-placeholder" :title="'icone: ui-' + (field.type || 'text')">
                                                <Icon :icon-class="iconFor(field.type)" size="13" style="color: var(--secondary);" />
                                            </div>
                                            <div class="field-list-body">
                                                <template v-if="editingFieldId === field.id">
                                                    <label :for="'inline-field-' + field.id" class="sr-only">{{ t('quotationFields-field_form-name_label') }}</label>
                                                    <input
                                                        :id="'inline-field-' + field.id"
                                                        :name="`fields[${field.id}][field_name]`"
                                                        type="text"
                                                        v-model="field.field_name"
                                                        class="form-control"
                                                        :ref="'editFieldInput-' + field.id"
                                                    />
                                                </template>
                                                <template v-else>
                                                    <div class="field-name">{{ field.field_name }}</div>
                                                    <div class="field-type"><span v-lang="field.type"></span></div>
                                                </template>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="field-actions">
                                        <button
                                            v-if="editingFieldId === field.id"
                                            @click="confirmEditFieldName(field)"
                                        >
                                            <Icon icon-class="ui-check" />
                                        </button>
                                        <button
                                            v-else
                                            @click="startEditingField(field)"
                                        >
                                            <Icon icon-class="ui-edit-05" :tooltip="t('quotationFields-tooltips-edit_field')" />
                                        </button>
                                        <button
                                            class="trash"
                                            @click="openConfirmDeleteQuotationField(field)"
                                        >
                                            <Icon icon-class="ui-trash-01" :tooltip="t('quotationFields-tooltips-delete_field')" />
                                        </button>
                                    </td>
                                </tr>
                            </table>
                            <button
                                class="add-btn"
                                @click.prevent="addNew"
                                :disabled="is('saving')"
                            >
                                <Icon icon-class="ui-plus" />
                                {{ t('quotationFields-add_button') }}
                            </button>
                        </template>
                        <template v-if="state === 'form'">
                            <h1>{{ t('quotationFields-create_form_title') }}</h1>
                            <form>
                                <div class="fields">
                                    <div class="field">
                                                <label for="new-field-name">{{ t('quotationFields-field_form-name_label') }}</label>
                                                <input
                                                    id="new-field-name"
                                                    name="field_name"
                                                    type="text"
                                                    v-model="form.field_name"
                                                    class="form-control"
                                                />
                                            </div>
                                            <div class="field mt-2">
                                                <label for="new-field-type">{{ t('quotationFields-field_form-type_label') }}</label>
                                                <select
                                                    id="new-field-type"
                                                    name="type"
                                                    v-model="form.type"
                                                    class="form-control"
                                                >
                                            <option
                                                value="text"
                                                v-lang="'text'"
                                            ></option>
                                            <option
                                                value="date"
                                                v-lang="'date'"
                                            ></option>
                                            <option
                                                value="user"
                                                v-lang="'user'"
                                            ></option>
                                            <option
                                                value="number"
                                                v-lang="'number'"
                                            ></option>
                                            <option
                                                value="textarea"
                                                v-lang="'textarea'"
                                            ></option>
                                        </select>
                                    </div>
                                </div>
                            </form>
                            <div class="actions">
                                <a
                                    class="btn"
                                    href="#"
                                    @click.prevent="setState('list')"
                                    >{{ t('quotationFields-back_button') }}</a
                                >
                                <button
                                    class="btn btn-success"
                                    @click.prevent="save"
                                    :disabled="is('saving')"
                                >
                                    <!--                  <span class="fa fa-spin fa-spinner" v-if="is('saving')"></span>-->
                                    {{ t('quotationFields-save_button') }}
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.table.fields td {
    vertical-align: middle;
}
.actions {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin-top: 20px;
}
.field-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    button {
        background: transparent;
        border: none;
        color: #1F2A37;
    }
    button.trash {
        color: var(--danger);
    }
}

.add-btn {
    display: flex;
    padding: 12px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    background: #FCFCFD;
    width: 100%;
}

.field-list-item {
    gap: 12px;
}
.field-icon-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 56px;
}
.field-icon-placeholder .placeholder-box {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px dashed #E5E7EB;
    background: #FFFFFF;
}
.field-icon-placeholder .placeholder-label {
    font-size: 11px;
    color: #6B7280;
    margin-top: 6px;
    text-align: center;
}
.field-list-body .field-name {
    font-weight: 600;
}
.field-list-body .field-type {
    font-size: 12px;
    color: #9CA3AF;
}

.table.fields td {
    vertical-align: middle;
    border: none;
}
</style>
<script>
import keviApi from "../keviApi";
import Icon from "../components/icon/icon";

export default {
    props: {
        user: Object,
    },
    components: {
        Icon
    },
    data: function () {
        return {
            state: "loading",
            fields: [],
            form: {
                id: null,
                field_name: null,
                type: null,
            },
            quotationFieldToShowInModal: {
                id: null,
                field_name: null,
            },
            segmentsRelations: [],
            automationsRelations: [],
            editingFieldId: null,
        };
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
    },
    async mounted() {
        document.getElementsByTagName("title")[0].innerText =
            this.t('quotationFields-page_title');
        await this.loadFields();
        this.setState("list");
    },
    methods: {
        iconFor(type) {
            switch (type) {
                case "date":
                    return "ui-calendar";
                case "user":
                    return "ui-user-square";
                case "number":
                    return "ui-number";
                case "textarea":
                    return "ui-type-square";
                case "checkbox":
                    return "ui-check-square-broken";
                case "coupon":
                    return "ui-ticket-01";
                default:
                    return "ui-type-square";
            }
        },
        async loadFields() {
            let options = keviApi.getOptions("/quotation-fields", "GET");
            const response = await this.$http(options);
            this.fields = JSON.parse(JSON.stringify(response.body));
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        addNew() {
            this.setState("form");
            this.form = {
                id: null,
                field_name: null,
                type: null,
            };
        },
        save() {
            const options = keviApi.getOptions("quotation-fields", "PUT", {
                name: this.form.field_name,
                type: this.form.type,
            });
            window?.mixpanel?.track("Settings - save quotation field");
            this.$http(options).then(async () => {
                this.$store.dispatch(
                    "notification/success",
                    this.t('quotationFields-notifications-added'),
                );
                this.setState("loading");
                await this.loadFields();
                this.setState("list");
            });
        },
        openConfirmDeleteQuotationField(quotationField) {
            const options = keviApi.getOptions("quotation-fields/" + quotationField.id + "/relations", "GET");
            this.$http(options).then((response) => {
                this.segmentsRelations = response.body.segments;
                this.automationsRelations = response.body.automations;
            });
            this.quotationFieldToShowInModal = quotationField;
            this.$refs["confirmDeleteQuotationField"].open();
        },
        deleteQuotationField(id) {
            const options = keviApi.getOptions("quotation-fields/" + id, "DELETE");
            window?.mixpanel?.track("Settings - delete client field");
            this.$http(options).then(() => {
                this.fields = this.fields.filter(
                    (quotationField) => quotationField.id !== id,
                );
                this.$store.dispatch(
                    "notification/success",
                    this.t('quotationFields-notifications-deleted'),
                );
                this.setState("list");
            });
        },
        startEditingField(field) {
            this.editingFieldId = field.id;
            this.$nextTick(() => {
                const refName = `editFieldInput-${field.id}`;
                const el = this.$refs[refName];
                // Vue may return an array for ref when used inside v-for in some setups
                const inputEl = Array.isArray(el) ? el[0] : el;
                if (inputEl && typeof inputEl.focus === "function") {
                    inputEl.focus();
                }
            });
        },
        confirmEditFieldName(field) {
            this.quotationFieldToShowInModal = { ...field };
            this.$refs["confirmEditQuotationField"].open();
        },
        editQuotationFieldName(id) {
            const options = keviApi.getOptions("quotation-fields/" + id, "PUT", {
                field_name: this.quotationFieldToShowInModal.field_name,
            });
            window?.mixpanel?.track("Settings - edit client field name");
            this.$http(options).then(() => {
                this.fields = this.fields.map((quotationField) => {
                    if (quotationField.id === id) {
                        quotationField.field_name =
                            this.quotationFieldToShowInModal.field_name;
                    }
                    return quotationField;
                });
                this.$store.dispatch(
                    "notification/success",
                    this.t('quotationFields-notifications-edited'),
                );
                this.editingFieldId = null;
            });
        },
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
};
</script>
